// drawerHost.tsx — single-drawer-at-a-time singleton.
//
// Closes DESIGN_POLICY.md §5 "Drawers do not stack" + boss-synthesis.md §13
// anti-pattern A-DRWR-STACK.
//
// Public API:
//   - useDrawerHost()       hook returning { openId, open(id, onClose), close(id) }
//   - <DrawerHostRoot/>     portal sentinel (no-op at runtime; reserved for
//                           future global UI like a "Back to dashboard" rail)
//
// Mechanism: when a Drawer opens, it calls `host.open(id, onCloseCallback)`.
// The host stores the (id, onClose) tuple. If a second drawer opens, the
// host calls the first drawer's onClose() and then registers the second.
// On unmount each Drawer calls `host.close(id)` so the host clears the slot.

import type { ReactNode } from 'react';
import { create } from 'zustand';

interface DrawerHostState {
  openId: string | null;
  /** Currently-open drawer's onClose callback. */
  _onClose: (() => void) | null;
  open(id: string, onClose: () => void): void;
  close(id: string): void;
}

export const useDrawerHost = create<DrawerHostState>((set, get) => ({
  openId: null,
  _onClose: null,
  open(id, onClose) {
    const prev = get();
    if (prev.openId && prev.openId !== id && prev._onClose) {
      prev._onClose();
    }
    set({ openId: id, _onClose: onClose });
  },
  close(id) {
    const cur = get();
    if (cur.openId === id) {
      set({ openId: null, _onClose: null });
    }
  },
}));

/**
 * <DrawerHostRoot /> — mounts the singleton. Currently a no-op; reserved as
 * the future portal node for any global drawer-cluster UI (e.g. an "open in
 * audit" preset switcher) without stacking. Kept as a real React node so
 * the boot wiring in `main.tsx` is symmetrical with `<LiveRegionProvider>`.
 */
export function DrawerHostRoot({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}
